/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ICONS from "../../../../assets/Icons";
import {
  resetBankLookupData,
  resetBankVerification,
  resetBankVerificationError,
  resetIFSCDetails,
} from "../../../../redux/features/sharedResources/sharedResources";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import { linkIdentifier } from "../../../../utils/linkIdentifier";
import Validation from "../../../../utils/validation";
import TextInputField from "../../../_utils/CommonInputFields/TextInputField";
import CommonBankLogo from "../../CommonBankLogo";
import { formatAccountNumber } from "../../FormatAccountNumber";

/**
 * BankDetails Component
 *
 * Handles:
 * - Bank account input
 * - IFSC validation
 * - Bank verification status
 * - Virtual account flow
 * - Preview mode
 * - Bank detail rendering
 *
 * @component
 *
 * @param {Function} BenEnableNextButton
 * Enable/disable next button in parent component.
 *
 * @param {Function} setVirtualAccount
 * Updates virtual account state.
 *
 * @param {Function} setManualVirtualAccount
 * Updates manual virtual account state.
 *
 * @param {Function} setShowDetails
 * Toggle details visibility.
 *
 * @param {Function} setPayload
 * Updates payload object.
 *
 * @param {Object} payload
 * Main form payload object.
 *
 * @param {string} payload.bank_account
 * Bank account number.
 *
 * @param {string} payload.ifsc_code
 * IFSC code.
 *
 * @param {string} payload.legalName
 * Legal/business name.
 *
 * @param {string} payload.skip_account_verification
 * Skip verification flag.
 *
 * @param {Function} setBankVerificationStatus
 * Updates bank verification status.
 *
 * @param {Function} setBankLookupNext
 * Enables/disables lookup next flow.
 *
 * @param {boolean} manualVirtualAccount
 * Indicates whether manual virtual account is enabled.
 *
 * @param {boolean} bankLookupNext
 * Controls next action after bank lookup.
 *
 * @param {Object} ifscData
 * IFSC lookup response object.
 *
 * @param {string} ifscData.branch
 * Branch name.
 *
 * @param {string} ifscData.city
 * City name.
 *
 * @param {string} ifscData.district
 * District name.
 *
 * @param {string} ifscData.address
 * Branch address.
 *
 * @param {string} ifscData.ifsc
 * IFSC code.
 *
 * @param {Function} setBankUserInfo
 * Stores processed bank information.
 *
 * @param {boolean} bankVerificationStatus
 * Indicates whether bank verification succeeded.
 *
 * @param {string} type
 * Component mode/type.
 * Example: "switch_account"
 *
 * @param {boolean} previewMode
 * Enables preview mode UI.
 *
 * @param {boolean} virtualAccount
 * Indicates virtual account state.
 *
 * @param {boolean} isIFSCValid
 * Indicates IFSC validation state.
 *
 * @param {number} activeStep
 * Current step index.
 *
 * @param {boolean} ownerAccIfscError
 * Indicates IFSC owner validation error.
 *
 * @param {boolean} ownerAccIfscLoading
 * Indicates IFSC owner validation loading state.
 *
 * @returns {JSX.Element}
 */

const BankDetails = ({
  headerText,
  BenEnableNextButton = () => {},
  setVirtualAccount = () => {},
  setManualVirtualAccount = () => {},
  setShowDetails = () => {},
  setPayload = () => {},
  payload = {},
  setBankVerificationStatus = () => {},
  setBankLookupNext = () => {},
  manualVirtualAccount = false,
  bankLookupNext = false,
  ifscData = {},
  setBankUserInfo = () => {},
  bankVerificationStatus = false,
  type = "",
  previewMode = false,
  virtualAccount = false,
  isIFSCValid = false,
  activeStep = 0,
  ownerAccIfscError = false,
  ownerAccIfscLoading = false,
  archivePreviewMode = false,
  setAssociatedNext = () => {},
}) => {
  const {
    verifyBankAccountError,
    verifyBankAccountData,
    verifyBankAccountLoading,
    verifiyBankAccountMessage,
    ifscDetailsData,
  } = useSelector((state) => state.sharedResources);

  const [customErrors, setCustomErrors] = useState({});

  const dispatch = useDispatch();
  const [accFocused, setAccFocused] = useState(false);
  const [ifscFocused, setIfscFocused] = useState(false);

  const modifyValue = (type, value) => {
    if (
      type === "pan_no" ||
      type === "bank_account" ||
      type === "ifsc_code" ||
      type === "gstin"
    ) {
      return value?.toUpperCase();
    }
    if (type === "email_id") {
      return value;
    }
    return value;
  };

  const allowedValue = (name, value) => {
    if (name === "bank_account") return Validation.allowedBankAccNum(value);
    if (name === "ifsc_code") return Validation.allowedBankIfsc(value);
    return true;
  };

  const validateAndRecordError = (name, value) => {
    let errors = { ...customErrors };
    if (name === "ifsc_code")
      errors = Validation.validateIfsc(name, value, errors);
    if (name === "bank_account")
      errors = Validation.validateBankAcc(name, value, errors);
    setCustomErrors(errors);
  };

  useEffect(() => {
    if (verifyBankAccountError === true) {
      BenEnableNextButton(true);
      setVirtualAccount(false);
    }
  }, [verifyBankAccountError]);

  const handleChange = (e) => {
    setPayload({
      ...payload,
      virtual: "",
    });
    let { name, value } = e.target;
    value = modifyValue(name, value);

    if (!allowedValue(name, value)) return;

    if (value === payload[name]) return;
    setManualVirtualAccount(false);
    setShowDetails(false);
    setAssociatedNext(false);

    if (allowedValue(name, value)) {
      validateAndRecordError(name, value);
      dispatch(resetBankVerificationError());
      dispatch(resetBankVerification());
      dispatch(resetIFSCDetails());
      let newObj;
      if (name === "bank_account") {
        newObj = {
          ...payload,
          [name]: value,
          ifsc_code: "",
          virtual_account_type: "",
        };
        setBankVerificationStatus(false);
        dispatch(resetBankLookupData());
        setVirtualAccount(false);
      } else {
        newObj = { ...payload, [name]: value };
        dispatch(resetBankLookupData());
        setVirtualAccount(false);
        setBankLookupNext(false);
        if (!value || value.length < 11) {
          setBankVerificationStatus(false);
        }
      }

      setPayload(newObj);
    }
    if (payload.bank_account?.length === 0) {
      setBankVerificationStatus(false);
      setVirtualAccount(false);
    }

    if (payload.ifsc_code?.length === 0 || payload.ifsc_code?.length < 11) {
      setBankVerificationStatus(false);
    }
  };

  useEffect(() => {
    if (verifyBankAccountError === true && manualVirtualAccount === true) {
      setPayload({
        ...payload,
        virtual: true,
      });
    }
    if (verifyBankAccountError === true) {
      setPayload({ ...payload, skip_account_verification: "Y" });
      if (manualVirtualAccount === false) {
        setBankVerificationStatus(false);
      }
      BenEnableNextButton(true);
    }

    if (verifyBankAccountData?.status === true) {
      let errors = { ...customErrors };
      delete errors["bank_account"];
      setCustomErrors(errors);
      setPayload({
        ...payload,
        acc_holder_name:
          verifyBankAccountData?.verification_details[0].full_name,
        skip_account_verification: "N",
      });
      setBankVerificationStatus(true);
      if (!verifyBankAccountLoading && bankLookupNext) {
        BenEnableNextButton(false);
      }
      setVirtualAccount(false);
    }
    if (
      verifyBankAccountData?.status === true &&
      verifyBankAccountData?.verification_details[0].is_virtual === "Y"
    ) {
      setVirtualAccount(true);
      setPayload({
        ...payload,
        acc_holder_name:
          verifyBankAccountData?.verification_details[0].legal_name,
        virtual_account_type:
          verifyBankAccountData?.verification_details[0].virtual_account_type,
      });
    }
  }, [verifyBankAccountData]);

  useEffect(() => {
    if (customErrors["bank_account"]) {
      setBankVerificationStatus(false);
    }
    if (customErrors["ifsc_code"]) {
      setBankVerificationStatus(false);
    }
    if (!verifyBankAccountLoading && customErrors["ifsc_code"]) {
      setBankLookupNext(false);
    }
  }, [customErrors]);

  useEffect(() => {
    const details = verifyBankAccountData?.verification_details?.[0] || {};
    const ifsc = ifscData || {};

    setBankUserInfo([
      {
        branch: details?.branch || ifsc?.branch || "",
        city: details?.city || ifsc?.city || "",
        district: details?.district || ifsc?.district || "",
        address: details?.address || ifsc?.address || "",
      },
    ]);
  }, [
    verifyBankAccountData?.verification_details?.[0]?.branch,
    verifyBankAccountData?.verification_details?.[0]?.city,
    verifyBankAccountData?.verification_details?.[0]?.district,
    verifyBankAccountData?.verification_details?.[0]?.address,
    ifscData?.branch,
    ifscData?.city,
    ifscData?.district,
    ifscData?.address,
  ]);

  const bankValuesFilled = () => {
    return (
      payload.bank_account?.length >= 9 &&
      payload.ifsc_code?.length === 11 &&
      /^[A-Z]{4}0[A-Z0-9]{6}$/.test(payload.ifsc_code)
    );
  };

  const showVerifiedBankDetails =
    bankValuesFilled() &&
    activeStep !== GlobalConfiguration?.lengthCheckThree &&
    bankVerificationStatus &&
    !customErrors["bank_account"] &&
    verifyBankAccountData?.verification_details?.length >
      GlobalConfiguration?.lengthCheckZero;

  const showManualVirtualBankDetails =
    bankValuesFilled() &&
    activeStep !== GlobalConfiguration?.lengthCheckThree &&
    manualVirtualAccount &&
    ifscData &&
    !showVerifiedBankDetails;

  const showExpandedBankDetails =
    showVerifiedBankDetails || showManualVirtualBankDetails;

  const isErrorBannerVisible =
    bankValuesFilled() &&
    activeStep !== GlobalConfiguration?.lengthCheckThree &&
    verifyBankAccountError &&
    !manualVirtualAccount;

  const getBankVerifiedElementDiv = (isLoading, bankDetails, ifscDetails) => {
    return (
      <div className={`w-full flex flex-col self-center gap-gap-md`}>
        <TextInputField
          variant={
            (!isLoading &&
              bankVerificationStatus &&
              bankDetails.value?.length >=
                GlobalConfiguration?.lengthCheckFour &&
              ifscDetails.value?.length === GlobalConfiguration?.ifscLength) ||
            (verifyBankAccountError === true && manualVirtualAccount === true)
              ? "verified"
              : !isLoading &&
                  bankDetails.value?.length >
                    GlobalConfiguration?.lengthCheckFour &&
                  verifyBankAccountError
                ? "error"
                : accFocused
                  ? "typing"
                  : "default"
          }
          placeholder={bankDetails.label}
          fontKind={"big"}
          bgColor="var(--primary-card)"
          fgColor="var(--text-primary)"
          value={bankDetails.value}
          name={bankDetails.name}
          onInput={handleChange}
          isFullWidth={true}
          adornment={isLoading ? ICONS?.LOADER_GIF : undefined}
          onFocus={(e) => setAccFocused(true)}
          onBlur={(e) => setAccFocused(false)}
          isDisabled={type === "switch_account"}
        />

        <TextInputField
          variant={
            (!isLoading &&
              bankVerificationStatus &&
              ifscDetails.value?.length === GlobalConfiguration?.ifscLength) ||
            (verifyBankAccountError === true && manualVirtualAccount === true)
              ? "verified"
              : !isLoading &&
                  (customErrors["ifsc_code"] || verifyBankAccountError) &&
                  ifscDetails.value?.length === GlobalConfiguration?.ifscLength
                ? "error"
                : ifscFocused
                  ? "typing"
                  : "default"
          }
          placeholder={ifscDetails.label}
          fontKind={"big"}
          bgColor="var(--primary-card)"
          fgColor="var(--text-primary)"
          value={ifscDetails.value}
          name={ifscDetails.name}
          onInput={handleChange}
          isDisabled={
            bankDetails.value?.length <
              GlobalConfiguration?.accountnormalLength ||
            type === "switch_account"
          }
          isFullWidth={true}
          adornment={isLoading ? ICONS?.LOADER_GIF : undefined}
          onFocus={(e) => setIfscFocused(true)}
          onBlur={(e) => setIfscFocused(false)}
        />
      </div>
    );
  };

  const bankVerificationDiv = () => {
    return (
      <div className="w-full">
        {getBankVerifiedElementDiv(
          verifyBankAccountLoading,
          {
            name: "bank_account",
            status: bankVerificationStatus,
            label: "Account number",
            value: payload.bank_account,
          },
          {
            name: "ifsc_code",
            status: bankVerificationStatus,
            label: "IFSC",
            value: payload.ifsc_code,
          },
        )}
      </div>
    );
  };

  if (previewMode) {
    const details = verifyBankAccountData?.verification_details?.[0];

    return (
      <div className="flex flex-col bg-primary-card rounded-border-radius-md gap-gap-md p-padding-md">
        <div className="text-text-primary font-semibold text-font-md">
          {headerText || "Bank Details"}
        </div>
        <div className="flex flex-col gap-gap-md">
          <div>
            <div className="flex flex-col gap-gap-md">
              <div className="flex flex-row justify-between items-center">
                <div className="flex flex-col gap-gap-xs">
                  <div className="w-width-full text-text-primary font-normal text-font-md">
                    {formatAccountNumber(
                      details?.bank_acc_no || payload?.bank_account,
                    )}
                  </div>
                  <div className="text-font-xs font-normal text-text-secondary">
                    Account number
                  </div>
                </div>

                <CommonBankLogo
                  ifsc={details?.ifsc_code || payload?.ifsc_code}
                  variant="big"
                  type="pill"
                />
              </div>
              <div className="flex flex-col justify-between">
                <div className="flex flex-col gap-gap-xs">
                  <div className="text-text-primary font-normal text-font-md">
                    {details?.ifsc_code || payload?.ifsc_code}
                  </div>
                  <div className="text-font-xs font-normal text-text-secondary">
                    IFSC
                  </div>
                </div>

                {(manualVirtualAccount ||
                  payload?.virtual_account_type === "automated") && (
                  <div className="flex flex-row justify-between items-center mt-margin-md">
                    <div className="flex flex-col gap-gap-xs">
                      <div className="text-text-primary font-normal text-font-md">
                        {manualVirtualAccount
                          ? "Manually created"
                          : "Auto detected"}
                      </div>
                      <div className="text-font-xs font-normal text-text-secondary">
                        Virtual account
                      </div>
                    </div>
                  </div>
                )}
                <div className="border-b border-primary-bg opacity-base-opacity my-margin-sm"></div>
              </div>
            </div>
            <div className="flex flex-col gap-gap-xs">
              <div className="w-width-full text-text-primary font-normal text-font-md">
                {details?.full_name ||
                  payload?.legal_name ||
                  payload?.legalName ||
                  payload?.account_name}
              </div>
              <div className="text-font-xs font-normal text-text-secondary">
                {verifyBankAccountData?.verification_details[0].is_virtual ===
                "Y"
                  ? "Account holder's name - Using legal name"
                  : "Account holder's name"}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-gap-md">
            <>
              {details || ifscDetailsData?.ifsc_details ? (
                <>
                  <div className="flex flex-row justify-between items-center">
                    <div className="flex flex-col gap-gap-xs">
                      <div className="text-text-primary font-normal text-font-md">
                        {details?.branch
                          ? details?.branch
                          : ifscDetailsData?.ifsc_details?.[
                                GlobalConfiguration.lengthCheckZero
                              ]?.branch
                            ? ifscDetailsData?.ifsc_details?.[
                                GlobalConfiguration.lengthCheckZero
                              ]?.branch
                            : "NA"}
                      </div>
                      <div className="text-font-xs font-normal text-text-secondary">
                        Branch
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-gap-xs">
                    <div className="w-width-full text-text-primary font-normal text-font-md ">
                      {details?.city
                        ? details?.city
                        : ifscDetailsData?.ifsc_details?.[0]?.city
                          ? ifscDetailsData?.ifsc_details?.[0]?.city
                          : "NA"}
                    </div>
                    <div className="text-font-xs  font-normal text-text-secondary">
                      City
                    </div>
                  </div>
                  <div className="flex flex-col gap-gap-xs">
                    <div className="w-width-full text-text-primary font-normal text-font-md ">
                      {details?.district
                        ? details?.district
                        : ifscDetailsData?.ifsc_details?.[0]?.district
                          ? ifscDetailsData?.ifsc_details?.[0]?.district
                          : "NA"}
                    </div>
                    <div className="text-font-xs  font-normal text-text-secondary">
                      District
                    </div>
                  </div>
                  <div className="flex flex-col gap-gap-xs">
                    <div
                      className="w-width-full text-text-primary  font-normal  text-font-md"
                      style={{
                        textWrap: "wrap",
                        wordWrap: "break-word",
                      }}
                    >
                      {details?.address
                        ? details?.address
                        : ifscDetailsData?.ifsc_details?.[0]?.address
                          ? ifscDetailsData?.ifsc_details?.[0]?.address
                          : "NA"}
                    </div>
                    <div className="text-font-xs  font-normal text-text-secondary">
                      Address
                    </div>
                  </div>
                </>
              ) : (
                <></>
              )}
            </>
          </div>
        </div>
      </div>
    );
  }

  if (archivePreviewMode) {
    return (
      <div className="w-full h-auto p-padding-md rounded-border-radius-md bg-primary-card custom-boxShadow">
        <div className="text-text-secondary">
          <div className="flex flex-col gap-gap-xs">
            <p className="text-text-primary font-semibold text-base">
              Archive Account Details
            </p>

            <p className="text-text-secondary font-normal text-xs">
              This account will be archived.
            </p>
          </div>

          <div className="border-b border-primary-bg w-full my-margin-sm" />

          <div className="flex flex-col gap-gap-xs">
            <h5 className="text-text-primary font-semibold text-base">
              {payload?.pan_no && payload?.gstin_detail
                ? "PAN & GSTIN Details"
                : "PAN Details"}
            </h5>

            <div className="flex flex-col gap-gap-xs">
              <span className="text-text-primary font-normal text-base">
                {payload?.pan_no}
              </span>

              <p className="text-text-secondary font-normal text-xs">
                PAN 
              </p>
            </div>

            {payload?.gstin_detail && (
              <div className="flex flex-col gap-gap-xs">
                <span className="text-text-primary font-normal text-base">
                  {payload?.gstin_detail}
                </span>

                <p className="text-text-secondary font-normal text-xs">
                  GSTIN
                </p>
              </div>
            )}
          </div>

          <div className="border-b border-primary-bg w-full my-margin-sm" />

          <div className="flex flex-col gap-gap-sm">
            <h5 className="text-text-primary font-semibold text-base">
              Bank Details
            </h5>

            <div className="flex flex-row justify-between items-center">
              <div className="flex flex-col gap-gap-xs">
                <span className="text-text-primary font-normal text-base">
                  {formatAccountNumber(payload?.old_bank_account)}
                </span>

                <p className="text-text-secondary font-normal text-xs">
                  Account number
                </p>
              </div>

              <CommonBankLogo
                ifsc={payload?.old_ifsc_code}
                variant="big"
                type="pill"
              />
            </div>

            <div className="flex flex-col gap-gap-xs">
              <span className="text-text-primary font-normal text-base">
                {payload?.old_ifsc_code}
              </span>

              <p className="text-text-secondary font-normal text-xs">
                IFSC
              </p>
            </div>

            {payload?.old_virtual_account_type && (
              <div className="flex flex-col gap-gap-xs">
                <span className="text-text-primary font-normal text-base">
                  {payload?.old_virtual_account_type === "automated"
                    ? "Auto detected"
                    : payload?.old_virtual_account_type === "manual"
                      ? "Manually created"
                      : "N/A"}
                </span>

                <p className="text-text-secondary font-normal text-xs">
                  Virtual account
                </p>
              </div>
            )}
          </div>

          <div className="border-b border-primary-bg w-full my-margin-sm" />

          <div className="flex flex-col gap-gap-sm">
            <h5 className="text-text-primary font-semibold text-base">
              Contact Details
            </h5>

            <div className="flex flex-col gap-gap-sm">
              <div className="flex flex-col gap-gap-xs">
                <span className="text-text-primary font-normal text-base">
                  {payload?.name}
                </span>

                <p className="text-text-secondary font-normal text-xs">
                    {verifyBankAccountData?.verification_details[0].is_virtual ===
                "Y"
                  ? "Account name - Using legal name"
                  : "Account name"}
                </p>
              </div>

              <div className="flex flex-col gap-gap-xs">
                <span className="text-text-primary font-normal text-base">
                  +91 {payload?.contact_no}
                </span>

                <p className="text-text-secondary font-normal text-xs">
                  Contact number
                </p>
              </div>

              <div className="flex flex-col gap-gap-xs">
                <p
                  id="email-id-firstLetter"
                  className="text-text-primary font-normal text-base overflow-hidden whitespace-nowrap overflow-ellipsis w-width-almost-full"
                >
                  {linkIdentifier(payload?.email_id)}
                </p>

                <p className="text-text-secondary font-normal text-xs">
                  Email Id
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-width-full flex flex-col overflow-hidden rounded-border-radius-md relative z-z-index-content ${
        virtualAccount && isIFSCValid ? "Qlg:mb-margin-base" : ""
      }`}
    >
      <div className="w-width-full rounded-border-radius-md relative z-z-index-content">
        <div
          className={`absolute w-width-full py-padding-md bottom-bottom-base transition-all duration-animation-fast ease-in-out transform ${
            showExpandedBankDetails || isErrorBannerVisible
              ? "rounded-b-none"
              : "rounded-b-border-radius-md"
          }`}
          style={{
            backgroundColor: showExpandedBankDetails
              ? "var(--positive-card)"
              : isErrorBannerVisible
                ? "var(--negative-card)"
                : "transparent",
          }}
        ></div>

        <div className="w-width-full flex flex-col bg-primary-card rounded-border-radius-md relative">
          <div className="flex flex-row justify-between px-padding-md py-padding-md text-text-primary text-font-md font-semibold">
            Bank Details
          </div>
          <div className="w-width-full flex flex-col px-padding-md pb-padding-md">
            {activeStep !== GlobalConfiguration.lengthCheckThree ? (
              <div className="w-width-full">{bankVerificationDiv()}</div>
            ) : (
              <div className="flex flex-col gap-gap-lg pb-padding-button-l">
                <div>
                  <div className="w-width-full antaliased font-normal text-text-primary self-center text-font-lg">
                    {payload?.bank_account}
                  </div>
                  <div className="antaliased text-text-secondary text-font-xs opacity-dark-opacity">
                    Account number
                  </div>
                </div>
                <div>
                  <div className="w-width-full antaliased font-normal text-text-primary self-center text-xl">
                    {payload?.ifsc_code}
                  </div>
                  <div className="antaliased text-text-secondary text-font-xs opacity-dark-opacity">
                    IFSC
                  </div>
                </div>
                {payload.skip_account_verification === "N" ? (
                  <>
                    <div className="h-[var(--border)] bg-white px-padding-lg opacity-dark-opacity"></div>
                    <div className="flex justify-between">
                      <div className="antaliased font-normal text-text-primary self-center text-xl">
                        {
                          verifyBankAccountData?.verification_details[0]
                            ?.full_name
                        }
                      </div>
                      <div className="antaliased text-text-secondary text-font-xs opacity-dark-opacity">
                        Account holders name
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="h-[var(--border)] bg-text-primary px-padding-lg opacity-dark-opacity"></div>
                    <div className="flex flex-row justify-between items-center gap-gap-button-m">
                      <div className="h-full flex flex-col justify-between p-padding-xs text-text-primary text-font-md">
                        <div className="text-md font-extrabold">
                          SKIPPING VERIFICATION
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <img
                          className="w-icon-md h-icon-md"
                          src={ICONS?.ERROR_WITH_WHITE_OUTLINED_CIRCLE}
                          alt="verified logo"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={`transition-transform duration-animation-fast ease-in-out transform ${
          showExpandedBankDetails || isErrorBannerVisible
            ? "translate-y-0 opacity-base-opacity"
            : "-translate-y-full opacity-opacity-none"
        }`}
      >
        {showVerifiedBankDetails ? (
          <div className="bg-primary-nav rounded-b-border-radius-md">
            <div className="rounded-b-border-radius-md flex flex-row bg-positive-card relative z-z-index-content">
              <div className="w-width-full">
                <div className="py-padding-md flex whitespace-nowrap px-padding-md gap-gap-sm text-font-md font-font-normal text-text-primary items-start">
                  <img
                    className="w-icon-md h-icon-md mt-margin-xs"
                    src={ICONS?.VERIFIED_WITH_WHITE_CIRCLE_GREEN}
                    alt="verified logo"
                  />
                  <div className="flex flex-col gap-gap-xs">
                    <div className="w-auto overflow-hidden text-text-primary break-words whitespace-normal">
                      {virtualAccount
                        ? payload?.legalName ||
                          payload?.acc_holder_name ||
                          verifyBankAccountData?.verification_details?.[0]
                            ?.full_name
                        : verifyBankAccountData?.verification_details?.[0]
                            ?.full_name}
                    </div>
                    <div className="text-font-xs text-text-primary font-normal">
                      {virtualAccount || payload?.virtual_account === "Y"
                        ? "Account holder's name - Using legal name"
                        : "Account holder's name"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-primary-nav rounded-b-border-radius-md flex w-width-full flex-col gap-gap-md justify-between overflow-hidden py-padding-md px-padding-md">
              <div className="text-font-md text-text-primary font-semibold">
                Address Details
              </div>
              <div className="flex flex-col gap-gap-md">
                <div className="flex w-full flex-row justify-between items-center">
                  <div className="flex flex-col gap-gap-xs">
                    <div className="text-font-md font-normal text-text-primary">
                      {verifyBankAccountData?.verification_details[0].branch
                        ? verifyBankAccountData?.verification_details[0].branch
                        : "N/A"}
                    </div>
                    <div className="text-font-xs font-normal text-text-secondary">
                      Branch
                    </div>
                  </div>
                  <div className="">
                    <CommonBankLogo
                      ifsc={payload?.ifsc_code}
                      variant="big"
                      type="pill"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-gap-xs">
                  <div className="text-font-md font-normal text-text-primary">
                    {verifyBankAccountData?.verification_details[0].city
                      ? verifyBankAccountData?.verification_details[0].city
                      : "N/A"}
                  </div>
                  <div className="text-font-xs font-normal text-text-secondary">
                    City
                  </div>
                </div>
                <div className="flex flex-col gap-gap-xs">
                  <div className="text-font-md font-normal text-text-primary">
                    {verifyBankAccountData?.verification_details[0].district
                      ? verifyBankAccountData?.verification_details[0].district
                      : "N/A"}
                  </div>
                  <div className="text-font-xs font-normal text-text-secondary">
                    District
                  </div>
                </div>
                <div className="flex flex-col gap-gap-xs">
                  <div
                    className="text-font-md font-normal text-text-primary"
                    style={{
                      wordWrap: "break-word",
                      textWrap: "wrap",
                    }}
                  >
                    {verifyBankAccountData?.verification_details[0].address
                      ? verifyBankAccountData?.verification_details[0].address
                      : "N/A"}
                  </div>
                  <div className="text-font-xs font-normal text-text-secondary">
                    Address
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : showManualVirtualBankDetails ? (
          <>
            <div className="bg-primary-nav rounded-b-border-radius-md">
              <div className="rounded-b-border-radius-md flex flex-row bg-positive-card relative z-z-index-content">
                <div className="w-width-full">
                  <div className="flex whitespace-nowrap px-padding-md py-padding-md gap-gap-sm text-font-md font-font-normal text-text-primary items-start">
                    <img
                      className="w-icon-md h-icon-md mt-margin-xs"
                      src={ICONS?.VERIFIED_WITH_WHITE_CIRCLE_GREEN}
                      alt="verified logo"
                    />
                    <div className="flex flex-col gap-gap-xs">
                      <div className="w-auto overflow-hidden text-text-primary break-words whitespace-normal">
                        {payload?.legalName || payload?.legal_name || "N/A"}
                      </div>
                      <div className="text-font-xs text-text-secondary">
                        Account holder's name - Using legal name
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-primary-nav rounded-b-border-radius-md flex w-width-full flex-col gap-gap-sm justify-between overflow-hidden p-padding-md">
                <div className="text-font-md font-normal text-text-primary">
                  Address Details
                </div>
                <div className="flex flex-col gap-gap-md">
                  <div className="flex w-full flex-row justify-between items-center">
                    <div className="flex flex-col gap-gap-xs">
                      <div className="text-font-md font-normal text-text-primary">
                        {ifscData?.branch ?? "N/A"}
                      </div>
                      <div className="text-font-xs font-normal text-text-secondary">
                        Branch
                      </div>
                    </div>
                    <div className="">
                      <CommonBankLogo
                        ifsc={ifscData?.ifsc}
                        variant="big"
                        type="pill"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-gap-xs">
                    <div className="text-font-md font-normal text-text-primary">
                      {ifscData?.city ?? "N/A"}
                    </div>
                    <div className="text-font-xs font-normal text-text-secondary">
                      City
                    </div>
                  </div>
                  <div className="flex flex-col gap-gap-xs">
                    <div className="text-font-md font-normal text-text-primary">
                      {ifscData?.district ?? "N/A"}
                    </div>
                    <div className="text-font-xs font-normal text-text-secondary">
                      District
                    </div>
                  </div>
                  <div className="flex flex-col gap-gap-xs">
                    <div
                      className="text-font-md font-normal text-text-primary"
                      style={{
                        wordWrap: "break-word",
                        textWrap: "wrap",
                      }}
                    >
                      {ifscData?.address ?? "N/A"}
                    </div>
                    <div className="text-font-xs font-normal text-text-secondary">
                      Address
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : isErrorBannerVisible ? (
          <div className="relative">
            <div className="bg-emphasis flex flex-col h-full">
              <div className="bg-negative-card px-padding-md py-padding-md flex rounded-b-border-radius-md">
                <div className="flex flex-row items-start gap-gap-button-m">
                  <div className="flex flex-col flex-shrink-0">
                    <img
                      className="w-icon-md h-icon-md mt-margin-xs"
                      src={ICONS?.ERROR_WITH_WHITE_OUTLINED_CIRCLE}
                      alt="verified logo"
                    />
                  </div>

                  <div className="h-height-full flex flex-col justify-between text-text-primary text-base">
                    <div className="text-font-md font-normal">
                      Verification failed
                    </div>
                    {verifyBankAccountError === true &&
                    ownerAccIfscError === false &&
                    !ownerAccIfscLoading ? (
                      <>
                        <p className=" text-text-secondary text-font-sm font-normal">
                          This account cannot be verified. It can still be used
                          by making it a virtual account.
                        </p>
                      </>
                    ) : (
                      <>
                        <ul className="list-disc flex flex-col text-font-xs pl-padding-md">
                          <li>Account could not be verified</li>
                          <li>Crosscheck the information given above</li>
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-emphasis w-width-full mx-auto rounded-b-border-radius-md flex px-padding-md py-padding-md">
              <div className="flex items-start flex-col gap-gap-xs">
                <div className="text-font-xs font-semibold text-text-primary">
                  Bank remark :
                </div>
                <div className="text-font-xs font-normal text-text-secondary">
                  {verifiyBankAccountMessage}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default BankDetails;
